/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { formatPrice } from "@lemmonhq/orders";
import type { CreditNoteItem } from "./types";

interface CreditNoteItemPickerProps {
  items: CreditNoteItem[];
  /** order_item_id → quantity to credit */
  selected: Record<number, number>;
  /** false when only a whole credit is possible: the list is read-only. */
  editable: boolean;
  currency: string;
  onChange: (selected: Record<number, number>) => void;
}

const formatQuantity = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

/** The sale's items with a quantity stepper each, capped at what is left. */
export const CreditNoteItemPicker = defineComponent<CreditNoteItemPickerProps>({
  name: "CreditNoteItemPicker",
  props: ["items", "selected", "editable", "currency", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const setQuantity = (item: CreditNoteItem, quantity: number) => {
      const clamped = Math.min(
        Math.max(quantity, 0),
        Number(item.available_quantity),
      );
      props.onChange({ ...props.selected, [item.order_item_id]: clamped });
    };

    const setAll = (full: boolean) => {
      props.onChange(
        Object.fromEntries(
          props.items.map((item) => [
            item.order_item_id,
            full ? Number(item.available_quantity) : 0,
          ]),
        ),
      );
    };

    const subtitle = (item: CreditNoteItem) => {
      const credited = Number(item.credited_quantity);
      return [
        `${formatQuantity(Number(item.quantity))} × ${formatPrice(
          Number(item.unit_price),
          true,
          2,
          props.currency,
        )}`,
        item.order_display_id
          ? t("reports.tx.reopen_table.order_number", [item.order_display_id])
          : null,
        credited > 0
          ? t("reports.tx.credit_note_items.credited", [
              formatQuantity(credited),
            ])
          : null,
      ]
        .filter(Boolean)
        .join(" · ");
    };

    return () => (
      <section>
        <div class="mb-1 flex items-center justify-between gap-2">
          <h3 class="text-sm font-semibold text-accent">
            {t("reports.tx.credit_note_items.items")}
          </h3>
          {props.editable ? (
            <div class="join">
              <button class="btn btn-xs join-item" onClick={() => setAll(true)}>
                {t("reports.tx.credit_note_items.select_all")}
              </button>
              <button
                class="btn btn-xs join-item"
                onClick={() => setAll(false)}
              >
                {t("reports.tx.credit_note_items.select_none")}
              </button>
            </div>
          ) : (
            <span class="text-xs text-base-content/60">
              {t("reports.tx.credit_note_items.whole_only")}
            </span>
          )}
        </div>
        <ul class="divide-y divide-base-300 rounded-box border border-base-300">
          {props.items.map((item) => {
            const available = Number(item.available_quantity);
            const quantity = props.selected[item.order_item_id] ?? 0;
            return (
              <li
                key={item.order_item_id}
                class={`flex items-center gap-3 px-3 py-2 ${
                  available <= 0 ? "opacity-50" : ""
                }`}
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-medium text-accent">
                    {item.product_name || `#${item.order_item_id}`}
                  </div>
                  <div class="truncate text-xs text-base-content/60">
                    {subtitle(item)}
                  </div>
                </div>
                {props.editable ? (
                  <div class="join shrink-0">
                    <button
                      class="btn btn-sm btn-square join-item"
                      disabled={quantity <= 0}
                      onClick={() => setQuantity(item, quantity - 1)}
                    >
                      <span class="material-symbols-outlined text-base">
                        remove
                      </span>
                    </button>
                    <span class="join-item flex w-9 items-center justify-center border-y border-base-300 font-mono text-sm">
                      {formatQuantity(quantity)}
                    </span>
                    <button
                      class="btn btn-sm btn-square join-item"
                      disabled={quantity >= available}
                      onClick={() => setQuantity(item, quantity + 1)}
                    >
                      <span class="material-symbols-outlined text-base">
                        add
                      </span>
                    </button>
                  </div>
                ) : (
                  <span class="shrink-0 font-mono text-sm">
                    {formatQuantity(available)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    );
  },
});
